// Build trigger 1
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { CompanyProvider } from './CompanyContext';
import { UserProvider, useUser } from './UserContext';
import EmpresaList from './EmpresaList';
import EmpresaDetail from './EmpresaDetail';
import EmpresaForm from './EmpresaForm';
import ProveedorList from './ProveedorList';
import ProveedorDetail from './ProveedorDetail';
import ProveedorForm from './ProveedorForm';
import RequisicionList from './RequisicionList';
import RequisicionDetail from './RequisicionDetail';
import RequisicionForm from './RequisicionForm';
import SolicitudCotizacionList from './SolicitudCotizacionList';
import SolicitudCotizacionDetail from './SolicitudCotizacionDetail';
import SolicitudCotizacionForm from './SolicitudCotizacionForm';
import OrdenCompraList from './OrdenCompraList';
import OrdenCompraDetail from './OrdenCompraDetail';
import OrdenCompraTemplateEditor from './OrdenCompraTemplateEditor';
import OrdenCompraEditor from './OrdenCompraEditor';
import Breadcrumbs from './Breadcrumbs';
import {
  CssBaseline, Box, Typography, Button, CircularProgress,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  styled, Toolbar, IconButton, ThemeProvider
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar from '@mui/material/AppBar';
import theme from './theme';
import GlobalCompanySelector from './GlobalCompanySelector';
import Dashboard from './Dashboard';
import Login from './Login';
import UserList from './UserList';
import UserForm from './UserForm';
import ExportData from './ExportData';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import DownloadIcon from '@mui/icons-material/Download';

const drawerWidth = 240;

// --- Styled Components for Collapsible Drawer ---

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  }),
);

// --- Main App Components ---

const PrivateRoute = ({ children }) => {
  const { user, loading } = useUser();
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress /></Box>;
  }
  return user ? children : <Navigate to="/login" />;
};

const HomeRedirect = () => <Dashboard />;

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const [open, setOpen] = useState(true);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userRoles = {
    canViewDashboard: user && ['ADMIN', 'SUPERADMIN'].includes(user.role),
    canViewRequisiciones: user && ['USER_A', 'USER_B', 'ADMIN', 'SUPERADMIN'].includes(user.role),
    canViewOCs: user && ['USER_B', 'ADMIN', 'SUPERADMIN'].includes(user.role),
    canViewProveedores: user && ['USER_A', 'USER_B', 'ADMIN', 'SUPERADMIN'].includes(user.role),
    canViewAdminSections: user && ['ADMIN', 'SUPERADMIN'].includes(user.role),
    isSuperAdmin: user && user.role === 'SUPERADMIN',
  };

  const navItems = [
    { text: 'Dashboard', to: '/', icon: <DashboardIcon />, condition: userRoles.canViewDashboard },
    { text: 'Requisiciones', to: '/requisiciones', icon: <ReceiptIcon />, condition: userRoles.canViewRequisiciones },
    { text: 'RFQs', to: '/solicitudes_cotizacion', icon: <RequestQuoteIcon />, condition: userRoles.canViewAdminSections },
    { text: 'Órdenes de Compra', to: '/ordenes-compra', icon: <ShoppingCartIcon />, condition: userRoles.canViewOCs },
    { text: 'Empresas', to: '/empresas', icon: <BusinessIcon />, condition: userRoles.canViewAdminSections },
    { text: 'Proveedores', to: '/proveedores', icon: <PeopleIcon />, condition: userRoles.canViewProveedores },
    { text: 'Manage Users', to: '/users', icon: <ManageAccountsIcon />, condition: userRoles.isSuperAdmin },
    { text: 'Export Data', to: '/export', icon: <DownloadIcon />, condition: userRoles.isSuperAdmin },
  ];

  if (location.pathname === '/login') {
    return <Login />;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={() => setOpen(!open)}
            edge="start"
            sx={{ marginRight: 5 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Vflow
          </Typography>
          {(userRoles.canViewAdminSections || user?.role === 'USER_B') && <GlobalCompanySelector />}
          <Button color="inherit" onClick={handleLogout}>Logout</Button>
        </Toolbar>
      </AppBar>
      
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={() => setOpen(false)}>
            <ChevronLeftIcon />
          </IconButton>
        </DrawerHeader>
        <List>
          {navItems.map((item) => item.condition && (
            <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                sx={{ minHeight: 48, justifyContent: open ? 'initial' : 'center', px: 2.5 }}
                component={Link} to={item.to}
                selected={(location.pathname.startsWith(item.to) && item.to !== '/') || location.pathname === item.to}
              >
                <ListItemIcon sx={{ minWidth: 0, mr: open ? 3 : 'auto', justifyContent: 'center' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>

      <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <DrawerHeader />
        <Breadcrumbs />
        <Routes>
          <Route path="/" element={<PrivateRoute><HomeRedirect /></PrivateRoute>} />
          <Route path="/requisiciones" element={<PrivateRoute><RequisicionList /></PrivateRoute>} />
          <Route path="/solicitudes_cotizacion" element={<PrivateRoute><SolicitudCotizacionList /></PrivateRoute>} />
          <Route path="/ordenes-compra" element={<PrivateRoute><OrdenCompraList /></PrivateRoute>} />
          <Route path="/empresas" element={<PrivateRoute><EmpresaList /></PrivateRoute>} />
          <Route path="/proveedores" element={<PrivateRoute><ProveedorList /></PrivateRoute>} />
          <Route path="/requisiciones/nueva" element={<PrivateRoute><RequisicionForm /></PrivateRoute>} />
          <Route path="/requisiciones/:id" element={<PrivateRoute><RequisicionDetail /></PrivateRoute>} />
          <Route path="/requisiciones/editar/:id" element={<PrivateRoute><RequisicionForm /></PrivateRoute>} />
          <Route path="/solicitudes_cotizacion/nueva" element={<PrivateRoute><SolicitudCotizacionForm /></PrivateRoute>} />
          <Route path="/solicitudes_cotizacion/:id" element={<PrivateRoute><SolicitudCotizacionDetail /></PrivateRoute>} />
          <Route path="/solicitudes_cotizacion/editar/:id" element={<PrivateRoute><SolicitudCotizacionForm /></PrivateRoute>} />
          <Route path="/ordenes-compra/:id" element={<PrivateRoute><OrdenCompraDetail /></PrivateRoute>} />
          <Route path="/ordenes-compra/:id/edit" element={<PrivateRoute><OrdenCompraEditor /></PrivateRoute>} />
          <Route path="/empresas/nueva" element={<PrivateRoute><EmpresaForm /></PrivateRoute>} />
          <Route path="/empresas/:id" element={<PrivateRoute><EmpresaDetail /></PrivateRoute>} />
          <Route path="/empresas/:id/template/oc" element={<PrivateRoute><OrdenCompraTemplateEditor /></PrivateRoute>} />
          <Route path="/empresas/editar/:id" element={<PrivateRoute><EmpresaForm /></PrivateRoute>} />
          <Route path="/proveedores/nuevo" element={<PrivateRoute><ProveedorForm /></PrivateRoute>} />
          <Route path="/proveedores/:id" element={<PrivateRoute><ProveedorDetail /></PrivateRoute>} />
          <Route path="/proveedores/editar/:id" element={<PrivateRoute><ProveedorForm /></PrivateRoute>} />
          <Route path="/users" element={<PrivateRoute><UserList /></PrivateRoute>} />
          <Route path="/users/new" element={<PrivateRoute><UserForm /></PrivateRoute>} />
          <Route path="/users/edit/:id" element={<PrivateRoute><UserForm /></PrivateRoute>} />
          <Route path="/export" element={<PrivateRoute><ExportData /></PrivateRoute>} />
        </Routes>
        <Box component="footer" sx={{ textAlign: 'center', p: 2, mt: 'auto' }}>
          <Typography variant="body2" color="text.secondary">
            © 2025 {' '}
            <a href="https://archive.org/details/beau-travail-1999" target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }}>
              Amsi Sánchez
            </a>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

const AppWrapper = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <Router>
      <UserProvider>
        <CompanyProvider>
          <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/*" element={<AppContent />} />
          </Routes>
        </CompanyProvider>
      </UserProvider>
    </Router>
  </ThemeProvider>
);

export default AppWrapper;
